/**
 * Cadena de detección de locale (en orden de prioridad):
 *   1. URL prefix (`/es/...` o `/en/...`)
 *   2. Cookie `boletra_locale`
 *   3. Locale del usuario autenticado (campo `users.locale`)
 *   4. Header `Accept-Language`
 *   5. Default (`es`)
 *
 * Esta lógica vive en `packages/i18n` para que web, scanner y API
 * usen exactamente el mismo orden de fallback.
 */

import { DEFAULT_LOCALE, isSupportedLocale, normalizeLocale, type Locale } from './index.js';

export const LOCALE_COOKIE_NAME = 'boletra_locale';

export interface DetectorInputs {
  url?: string;
  cookie?: string;
  userLocale?: string | null | undefined;
  acceptLanguage?: string | null | undefined;
}

export function detectFromUrl(url: string | undefined): Locale | undefined {
  if (!url) return undefined;
  const match = url.match(/^\/([a-z]{2})(?:\/|$)/i);
  if (!match || !match[1]) return undefined;
  const candidate = match[1].toLowerCase();
  return isSupportedLocale(candidate) ? candidate : undefined;
}

export function detectFromCookie(cookieHeader: string | undefined): Locale | undefined {
  if (!cookieHeader) return undefined;
  const match = cookieHeader.match(new RegExp(`(?:^|; )${LOCALE_COOKIE_NAME}=([a-z-]+)`, 'i'));
  if (!match || !match[1]) return undefined;
  const value = match[1].toLowerCase();
  return isSupportedLocale(value) ? value : undefined;
}

export function detectFromAcceptLanguage(header: string | undefined): Locale | undefined {
  if (!header) return undefined;
  const candidates = header
    .split(',')
    .map((entry) => {
      const parts = entry.trim().split(';');
      const tag = parts[0] ?? '';
      const q = parts[1] ?? 'q=1';
      const quality = Number.parseFloat(q.replace('q=', '')) || 1;
      return { tag: tag.trim().toLowerCase().split('-')[0] ?? '', quality };
    })
    .sort((a, b) => b.quality - a.quality);

  for (const { tag } of candidates) {
    if (isSupportedLocale(tag)) return tag;
  }
  return undefined;
}

export function detectLocale(inputs: DetectorInputs): Locale {
  return (
    detectFromUrl(inputs.url) ??
    detectFromCookie(inputs.cookie) ??
    (inputs.userLocale ? normalizeLocale(inputs.userLocale) : undefined) ??
    detectFromAcceptLanguage(inputs.acceptLanguage ?? undefined) ??
    DEFAULT_LOCALE
  );
}
